import { parseSse, type SseEvent } from './parser';

/* Fetch-based SSE consumer for the Bearer-gated admin stream.
 *
 * Loops: open `GET {path}` with the access token + `Last-Event-ID`,
 * stream-decode the body, emit parsed events, and reconnect on drop —
 * resuming from the last id so the bus replays in-window events
 * (etap 10 retention). A 401 triggers one refresh attempt before
 * reconnecting. Aborts cleanly when the returned dispose fn is called
 * (e.g. React unmount). */

export interface StreamOptions {
  path: string;
  getToken: () => string | null;
  onEvent: (ev: SseEvent) => void;
  /** Try to refresh the session on 401. Resolve true if a new token
      is now available. */
  onUnauthorized?: () => Promise<boolean>;
  /** Called when the session is definitively invalid (refresh keeps failing).
      The app should clear auth + redirect to /login. */
  onAuthInvalidated?: () => void;
  /** Called after each successful (re)connect. Consumers resync REST state
      here so a gap longer than the bus retention window (or a lag-forced
      reconnect) is reconciled rather than silently lost. */
  onOpen?: () => void;
  /** Reconnect backoff in ms (overridable for tests). */
  reconnectDelayMs?: number;
}

/** How many consecutive refresh failures before we treat the session as
    definitively dead. A single transient 5xx/502 during a backend deploy swap
    must NOT permanently silence the stream — only a persistent failure does. */
const MAX_REFRESH_FAILURES = 3;

export function subscribeStream(opts: StreamOptions): () => void {
  const reconnectDelay = opts.reconnectDelayMs ?? 2000;
  const controller = new AbortController();
  let lastEventId: string | null = null;
  let stopped = false;
  let refreshFailures = 0;

  const sleep = (ms: number) =>
    new Promise<void>((resolve) => setTimeout(resolve, ms));

  const run = async () => {
    while (!stopped) {
      try {
        const token = opts.getToken();
        const headers: Record<string, string> = {
          accept: 'text/event-stream',
        };
        if (token) headers.authorization = `Bearer ${token}`;
        if (lastEventId) headers['last-event-id'] = lastEventId;

        const resp = await fetch(opts.path, {
          method: 'GET',
          headers,
          credentials: 'include',
          signal: controller.signal,
        });

        if (resp.status === 401 && opts.onUnauthorized) {
          const ok = await opts.onUnauthorized();
          if (ok) {
            refreshFailures = 0;
            continue; // retry immediately with the refreshed token
          }
          // Refresh failed. Don't permanently stop on a single hiccup — a
          // transient 5xx during a deploy swap resolves on the next attempt.
          // Only after repeated failure is the session truly dead.
          refreshFailures += 1;
          if (refreshFailures >= MAX_REFRESH_FAILURES) {
            opts.onAuthInvalidated?.();
            return;
          }
          await sleep(reconnectDelay);
          continue; // reconnect → re-attempts refresh
        }
        if (!resp.ok || !resp.body) {
          await sleep(reconnectDelay);
          continue;
        }

        // Connected. Reset the failure counter and let consumers resync REST
        // state to fill any gap the replay buffer couldn't cover.
        refreshFailures = 0;
        opts.onOpen?.();

        const reader = resp.body.getReader();
        const decoder = new TextDecoder();
        let buffer = '';
        // Pump until the server closes the stream or we're aborted.
        for (;;) {
          const { done, value } = await reader.read();
          if (done) break;
          buffer += decoder.decode(value, { stream: true });
          const { events, rest } = parseSse(buffer);
          buffer = rest;
          for (const ev of events) {
            if (ev.id) lastEventId = ev.id;
            opts.onEvent(ev);
          }
        }
      } catch {
        if (controller.signal.aborted) return;
        // Network blip — fall through to the backoff + reconnect.
      }
      if (stopped) return;
      await sleep(reconnectDelay);
    }
  };

  void run();

  return () => {
    stopped = true;
    controller.abort();
  };
}
